/**
 * The album slider on the pricing peek.
 *
 * Display only -- nothing here adds to cart. All arithmetic comes from
 * rf-pricing.js, which is pinned by tests against docs/product-spec.md; this
 * file only moves numbers into the DOM.
 *
 * It preserves the `?albums=N` handoff the design was built around: the CTA
 * carries the chosen count through to the order builder, which reads it back.
 * See CLAUDE.md, "The old theme's order builder".
 *
 * Copy templates come from data attributes so the wording stays editable in the
 * theme editor. {percent} and {amount} are substituted.
 *
 * Tier cards ([data-rf-tier-card]) are optional and scoped to their own
 * calculator. The homepage has none, so every loop below is over an empty list
 * and its behaviour is unchanged. Each card's price is written from the same
 * perAlbum() the slider readout uses -- the card carries a count, never a price,
 * so it cannot advertise a rate the cart will not honour.
 */
(function () {
  'use strict';

  /* ── THE SKIP LIST (plan decision D6, unit U6) ────────────────────────────
     What this pass deliberately did NOT simplify, written down so the next
     session does not re-litigate it:

       · the five boot/bind scaffolds are KEPT. Five self-contained copies of a
         twelve-line scaffold are simpler than one shared module plus a
         load-order dependency in five files.
       · both UMD wrappers are KEPT -- rf-pricing.js and rf-order-payload.js,
         the two files that expose the same api object as `window.Photokive*`
         AND as `module.exports`. (The export-and-return escape hatch below is a
         different pattern, and U6 added a third copy of it to this file.)
       · the FOUR count parsers (clampAlbums, clampSets, orderAlbums, parseCount)
         and the two money formatters (money, moneyFromCents) are KEPT. All six
         live in rf-pricing.js, are documented as deliberate, and are pinned by
         tests. (This read "the three count parsers (clampAlbums, clampSets,
         parseCount) ... All five" until 3 Sep 2026. It was true when written and
         stopped being true in U2 of the zero-albums pass, which added
         `orderAlbums` -- the builder's floor-0 album count -- and folded the
         shared body into a private `clampCount(value, floor)` that is NOT
         exported. So the skip list under-counted the exports it is a skip list
         FOR, which is the one thing it cannot afford to do. The three clamps
         are now one-line delegations to `clampCount`; that fold IS the
         simplification this entry used to refuse, and it was taken because a
         second floor was needed rather than to tidy.)
       · all four `:has()` selectors in the order builder are KEPT.
       · the `fill()` FOLD IS NOT TAKEN. There is no load-order-safe home for a
         shared copy other than rf-pricing.js, which is the wrong module for
         string templating. tests/order-builder-strings.test.js carries an
         equivalence table over every placeholder-bearing schema default
         instead -- documentation of where the two implementations agree, and of
         the three inputs where they do not.
       · the cross-file array-idiom differences are KEPT. Each file is already
         consistent within itself.

     Also not taken: folding rf-order-cart.js's showError/hideError pair into one
     toggling helper. tests/listener-placement.test.js asserts the literal call
     `hideError(form)` inside the module-level pageshow listener, and D9 gives
     the show path scrollIntoView + focus that the hide path must not have.

     Taken: the five `if (window.console && window.console.error)` guards in the
     two order files are now plain `console.error(...)`. Every browser this theme
     targets has `console` -- it already relies on `:has()`.
     ──────────────────────────────────────────────────────────────────────── */

  // Same escape hatch rf-order-builder.js and rf-order-cart.js use, and for the
  // same reason: fill() is pure, and a pure function reachable only through a
  // headless browser has a slow, flaky feedback loop. Only fill() is exported;
  // everything else in this file reads or writes the DOM. No build step.
  var isCommonJS = typeof module === 'object' && module.exports;
  var pricing = isCommonJS ? null : window.PhotokivePricing;

  if (isCommonJS) {
    module.exports = { fill: fill };
    return;
  }

  /**
   * Substitute {percent}, {amount} and {count} in a merchant template.
   *
   * MODULE SCOPE, not inside init(): it closes over nothing, and the hatch above
   * cannot export a function declared inside a per-instance initialiser.
   *
   * NOT INTERCHANGEABLE with rf-order-builder.js's fill(), which carries the
   * same name and a split/join implementation -- see the comment above that
   * declaration. This one takes BARE keys and fills only the three tokens its
   * regex names, so a fourth token passes through literally, a token missing
   * from `values` renders the word "undefined", and a value that itself
   * contains a token is not re-scanned. All three differences are pinned, with
   * both outputs spelled out, in tests/order-builder-strings.test.js.
   */
  function fill(str, values) {
    return str.replace(/\{(percent|amount|count)\}/g, function (_, key) {
      return values[key];
    });
  }

  function setup(root) {
    var scope = root && root.querySelector ? root : document;
    var calcs = scope.querySelectorAll('[data-rf-calc]');

    // A section root can itself be the calculator when the editor re-renders it.
    var list = Array.prototype.slice.call(calcs);
    if (scope !== document && scope.matches && scope.matches('[data-rf-calc]')) {
      list.push(scope);
    }

    list.forEach(init);
  }

  function init(calc) {
    if (!pricing || calc.dataset.rfBound === 'true') return;

    var slider = calc.querySelector('[data-rf-slider]');
    if (!slider) return;
    calc.dataset.rfBound = 'true';

    var el = {
      count: calc.querySelector('[data-rf-count]'),
      word: calc.querySelector('[data-rf-word]'),
      per: calc.querySelector('[data-rf-per]'),
      total: calc.querySelector('[data-rf-total]'),
      tier: calc.querySelector('[data-rf-tier]'),
      save: calc.querySelector('[data-rf-save]'),
      fill: calc.querySelector('[data-rf-fill]'),
      cta: calc.querySelector('[data-rf-cta]'),
    };

    // The tier row is a SIBLING of .rf-calc, not a descendant -- the design
    // renders it as its own panel beneath the calculator. So the lookup starts
    // at the shared .rf-wrap, not at the calculator, or it finds nothing.
    // Still scoped to this section's wrap rather than the document, so two
    // calculators on one page cannot cross-wire.
    var scope = calc.closest('.rf-wrap') || calc.parentElement || calc;
    var cards = Array.prototype.slice.call(scope.querySelectorAll('[data-rf-tier-card]'));

    var min = Number(slider.min) || 1;
    var max = Number(slider.max) || 20;
    var thumb = Number(calc.dataset.rfThumb) || 36;

    var tpl = {
      tier: calc.dataset.rfTierTpl || 'Save {percent}% per album',
      tierNone: calc.dataset.rfTierNoneTpl || 'Standard rate',
      save: calc.dataset.rfSaveTpl || 'You save {amount}',
      saveNone: calc.dataset.rfSaveNoneTpl || 'A lovely start',
    };

    // A bare "#" is the placeholder a section uses before its target page
    // exists. Appending to it would produce "#?albums=10", so leave it alone
    // until there is a real destination.
    var ctaBase = el.cta ? el.cta.getAttribute('href') || '' : '';
    if (ctaBase === '#' || ctaBase === '') ctaBase = null;

    function render() {
      var n = pricing.clampAlbums(slider.value);
      var per = pricing.perAlbum(n);
      var pct = pricing.savedPercent(n);
      var saved = pricing.savedTotal(n);

      if (el.count) el.count.textContent = n;
      if (el.word) el.word.textContent = pricing.albumWord(n);
      if (el.per) el.per.textContent = pricing.money(per);
      if (el.total) el.total.textContent = pricing.money(pricing.albumTotal(n));

      if (el.tier) {
        el.tier.textContent = pct > 0
          ? fill(tpl.tier, { percent: pct, amount: pricing.money(saved), count: n })
          : tpl.tierNone;
      }

      if (el.save) {
        el.save.textContent = saved > 0
          ? fill(tpl.save, { percent: pct, amount: pricing.money(saved), count: n })
          : tpl.saveNone;
      }

      if (el.fill) {
        var frac = max > min ? (n - min) / (max - min) : 0;
        el.fill.style.width =
          'calc(' + thumb / 2 + 'px + (100% - ' + thumb + 'px) * ' + frac + ')';
      }

      // The handoff the order builder reads back.
      if (el.cta && ctaBase) {
        el.cta.href = ctaBase + (ctaBase.indexOf('?') === -1 ? '?' : '&') + 'albums=' + n;
      }

      // A count no card shows simply leaves them all inactive.
      var chosen = false;
      for (var i = 0; i < cards.length; i++) {
        var active = cardCount(cards[i]) === n;
        if (active) chosen = true;
        cards[i].classList.toggle('is-active', active);
        cards[i].setAttribute('aria-pressed', active ? 'true' : 'false');
      }

      // Publish "one of these cards is the chosen one" for CSS to read. A card
      // cannot see its siblings' state in CSS without :has(), and this loop has
      // already worked the answer out -- so it is published rather than
      // re-derived per card. blocks/tier-card.liquid uses it to drop the highlighted
      // card back to its ribbon and a hairline so it stops competing with the
      // chosen one; when no card matches the count, nothing competes and the
      // highlight reads exactly as it always has. The flag goes on `scope`
      // rather than on the row, because `scope` is where the cards were queried
      // from and is therefore an ancestor of every one of them by construction.
      scope.classList.toggle('rf-has-tier-selection', chosen);

      // Keep the accessible value in step with the visible one.
      slider.setAttribute('aria-valuenow', String(n));
      slider.setAttribute(
        'aria-valuetext',
        n + ' ' + pricing.albumWord(n) + ', ' + pricing.money(per) + ' each'
      );
    }

    // Junk in the attribute clamps rather than reaching a price as NaN.
    function cardCount(card) {
      return pricing.clampAlbums(card.getAttribute('data-rf-tier-card'));
    }

    cards.forEach(function (card) {
      var count = cardCount(card);

      // The rate at this count never changes, so it is written once at bind
      // rather than recomputed on every slider tick.
      var priceEl = card.querySelector('[data-rf-tier-price]');
      if (priceEl) priceEl.textContent = pricing.money(pricing.perAlbum(count));

      card.addEventListener('click', function () {
        // Read the attribute again rather than trusting the bind-time value, so
        // a card whose count changed under us -- a theme-editor edit, a script
        // rewriting the attribute -- moves the slider to what it now says.
        var wanted = Math.min(Math.max(cardCount(card), min), max);
        if (Number(slider.value) === wanted) return;
        slider.value = String(wanted);
        // Dispatch rather than call render() directly: render is already bound
        // to 'input', so this renders exactly once, and anything else that ever
        // listens to the slider stays in step.
        slider.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });

    slider.addEventListener('input', render);
    slider.addEventListener('change', render);

    // An inbound ?albums=N wins over the section's starting value, so the two
    // halves of the flow agree when a customer comes back.
    var inbound = new URLSearchParams(window.location.search).get('albums');
    if (inbound !== null) {
      var wanted = pricing.clampAlbums(inbound);
      slider.value = String(Math.min(Math.max(wanted, min), max));
    }

    render();
  }

  function boot() {
    setup(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    setup(event.target);
  });
})();
